import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@availity/element';
import { useEffectAsync } from '@availity/hooks';

import { getOaaOrgs } from '@/api';
import { useAppContext } from '@/context';
import { trackEvent } from '@/global';
import {
  OAAAdminChange,
  OAAAdminChangeReview,
  OAAAdminCheckIn,
  OAAFeedback,
  OAASelectedOrganizations,
  OAASkip,
} from '@/pages';

const reasonOptions = [
  {
    label: 'I am no longer the primary administrator, but the organization is still active.',
    value: '0',
  },
  {
    label: 'I no longer work at this organization, but the organization is still active.',
    value: '1',
  },
  {
    label: 'Not needed - we use another organization for our Availity business.',
    value: '2',
  },
  {
    label: 'This business is closed.',
    value: '3',
  },
  {
    label: 'Other',
    value: '4',
  },
];

const OAA = () => {
  const { oaaOrgsResponse, setOaaOrgsResponse, selectedOAAOrgIds, setSelectedOAAOrgIds } = useAppContext();
  const [currentOAAPage, setCurrentOAAPage] = useState('adminCheckIn');
  const [skipBackPage, setSkipBackPage] = useState('adminCheckIn');
  const [hasCheckedOAABox, setHasCheckedOAABox] = useState(false);
  const [adminChangeFormValues, setAdminChangeFormValues] = useState({});

  const navigate = useNavigate();

  // in the event a user lands on this page manually and no OAA get call has been made yet, need to do so here
  useEffectAsync(async () => {
    if (!oaaOrgsResponse) {
      let response;

      try {
        response = await getOaaOrgs();
        if (response) {
          const { oaaOrganizations } = response;
          setOaaOrgsResponse(response);

          // all organizations should be selected by default; initialize state
          const orgIds = oaaOrganizations.map((organization) => organization.appId);
          setSelectedOAAOrgIds([...orgIds]);
        }
      } catch (error) {
        trackEvent({
          api: 'organization-agreement-api',
          event: 'get-oaa-orgs-failure',
          status: error?.response?.status,
        });

        // log the user out to clean their session for a fresh login in the event a 302 returns
        if (error?.response?.status === 302) {
          navigate('/logout');
        }
      }
    }
  }, []);

  const pageToDisplay = useMemo(() => {
    let component;

    if (oaaOrgsResponse && selectedOAAOrgIds) {
      switch (currentOAAPage) {
        case 'adminCheckIn': {
          component = <OAAAdminCheckIn setCurrentOAAPage={setCurrentOAAPage} setSkipBackPage={setSkipBackPage} />;
          break;
        }
        case 'skipOAA': {
          component = <OAASkip setCurrentOAAPage={setCurrentOAAPage} backPage={skipBackPage} />;
          break;
        }
        case 'viewSelectedOrgs': {
          component = (
            <OAASelectedOrganizations
              setCurrentOAAPage={setCurrentOAAPage}
              setSkipBackPage={setSkipBackPage}
              hasCheckedOAABox={hasCheckedOAABox}
              setHasCheckedOAABox={setHasCheckedOAABox}
            />
          );
          break;
        }
        case 'adminChange': {
          component = (
            <OAAAdminChange
              setCurrentOAAPage={setCurrentOAAPage}
              setSkipBackPage={setSkipBackPage}
              adminChangeFormValues={adminChangeFormValues}
              setAdminChangeFormValues={setAdminChangeFormValues}
              reasonOptions={reasonOptions}
            />
          );
          break;
        }
        case 'adminChangeReview': {
          component = (
            <OAAAdminChangeReview
              setCurrentOAAPage={setCurrentOAAPage}
              setSkipBackPage={setSkipBackPage}
              adminChangeFormValues={adminChangeFormValues}
              reasonOptions={reasonOptions}
            />
          );
          break;
        }
        case 'feedback': {
          component = <OAAFeedback />;
          break;
        }
        default:
      }
    }

    return component;
  }, [currentOAAPage, oaaOrgsResponse, selectedOAAOrgIds]);

  return <Box role="main">{pageToDisplay}</Box>;
};

export default OAA;
